<template>
  <DefaultPage title="Statistik">
    <div class="stats-container">
      <div class="stats-tabs category-tabs">
        <button
          v-for="cat in categoryTabs"
          :key="cat.id"
          class="stats-tab"
          :class="{ active: activeCategory === cat.id }"
          @click="goToCategory(cat)"
        >
          {{ cat.label }}
        </button>
      </div>

      <div class="stats-tabs">
        <button
          v-for="tab in currentTabs"
          :key="tab.id"
          class="stats-tab"
          :class="{ active: activeTab === tab.id }"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- ÖVERSIKT TAB -->
      <div v-if="activeTab === 'overview'" class="content overview-content">
        <BaseLoadingSpinner v-if="loadingAh && !analyticsData" />
        <template v-else>
          <YearPicker
            :years="overviewYears"
            :model-value="overviewYear"
            @update:model-value="overviewYear = $event"
          />
          <div v-if="activeCategory === 'all'" class="stat-cards">
            <div v-if="baAccess" class="stat-card">
              <div class="stat-card-label">Godkännandegrad — Byten</div>
              <div class="stat-card-value">
                {{ approvalRates.ba == null ? '–' : approvalRates.ba + '%' }}
              </div>
            </div>
            <div v-if="ahAccess" class="stat-card">
              <div class="stat-card-label">Godkännandegrad — Andrahand</div>
              <div class="stat-card-value">
                {{ approvalRates.ah == null ? '–' : approvalRates.ah + '%' }}
              </div>
            </div>
            <div v-if="ovPrivateAccess" class="stat-card">
              <div class="stat-card-label">
                Godkännandegrad — Överlåtelser privat
              </div>
              <div class="stat-card-value">
                {{
                  approvalRates.ovPrivate == null
                    ? '–'
                    : approvalRates.ovPrivate + '%'
                }}
              </div>
            </div>
            <div v-if="ovCompanyAccess" class="stat-card">
              <div class="stat-card-label">
                Godkännandegrad — Överlåtelser företag
              </div>
              <div class="stat-card-value">
                {{
                  approvalRates.ovCompany == null
                    ? '–'
                    : approvalRates.ovCompany + '%'
                }}
              </div>
            </div>
          </div>

          <div class="overview-chart-row">
            <div class="overview-chart-card wide">
              <h4 class="overview-chart-title">
                Ärendevolym per månad
                {{ overviewYear === 'all' ? '' : overviewYear }}
              </h4>
              <div class="stacked-chart">
                <div class="chart-y-axis">
                  <span v-for="tick in yTicks" :key="tick" class="y-tick">
                    {{ tick }}
                  </span>
                </div>
                <div class="chart-bars">
                  <div
                    v-for="m in selectedMonthlyData"
                    :key="m.month"
                    class="bar-group"
                  >
                    <div class="stacked-bar">
                      <div
                        v-if="m.ba"
                        class="bar-segment ba"
                        :style="{
                          height: (overviewBarsAnimated ? barH(m.ba) : 0) + '%'
                        }"
                        :data-tooltip="overviewBarTooltip(m, 'ba', 'Byten')"
                      ></div>
                      <div
                        v-if="m.ah"
                        class="bar-segment ah"
                        :style="{
                          height: (overviewBarsAnimated ? barH(m.ah) : 0) + '%'
                        }"
                        :data-tooltip="overviewBarTooltip(m, 'ah', 'Andrahand')"
                      ></div>
                      <div
                        v-if="m.ovPrivate"
                        class="bar-segment ov-private"
                        :style="{
                          height:
                            (overviewBarsAnimated ? barH(m.ovPrivate) : 0) + '%'
                        }"
                        :data-tooltip="
                          overviewBarTooltip(
                            m,
                            'ovPrivate',
                            'Överlåtelser privat'
                          )
                        "
                      ></div>
                      <div
                        v-if="m.ovCompany"
                        class="bar-segment ov-company"
                        :style="{
                          height:
                            (overviewBarsAnimated ? barH(m.ovCompany) : 0) + '%'
                        }"
                        :data-tooltip="
                          overviewBarTooltip(
                            m,
                            'ovCompany',
                            'Överlåtelser företag'
                          )
                        "
                      ></div>
                    </div>
                    <span class="bar-label">{{ monthLabel(m.month) }}</span>
                  </div>
                </div>
              </div>
              <div class="chart-legend">
                <span v-if="visibleCats.ba" class="legend-item">
                  <span class="legend-dot ba"></span> Byten
                </span>
                <span v-if="visibleCats.ah" class="legend-item">
                  <span class="legend-dot ah"></span> Andrahand
                </span>
                <span v-if="visibleCats.ovPrivate" class="legend-item">
                  <span class="legend-dot ov-private"></span> Överlåtelser
                  privat
                </span>
                <span v-if="visibleCats.ovCompany" class="legend-item">
                  <span class="legend-dot ov-company"></span> Överlåtelser
                  företag
                </span>
              </div>
            </div>

            <div class="overview-side">
              <div
                v-if="activeCategory !== 'all'"
                class="overview-chart-card stat-card"
              >
                <div class="stat-card-label">{{ singleStatLabel }}</div>
                <div class="stat-card-value">
                  {{ singleStatValue == null ? '–' : singleStatValue + '%' }}
                </div>
              </div>
              <div
                v-if="activeCategory === 'all'"
                class="overview-chart-card narrow"
              >
                <h4 class="overview-chart-title">
                  Fördelning ärendetyp
                  {{ overviewYear === 'all' ? '' : overviewYear }}
                </h4>
                <div class="donut-wrapper">
                  <svg viewBox="0 0 120 120" class="donut-svg">
                    <circle
                      v-for="(seg, i) in donutSegments"
                      :key="i"
                      class="donut-segment"
                      cx="60"
                      cy="60"
                      r="45"
                      fill="none"
                      :stroke="seg.color"
                      stroke-width="18"
                      :stroke-dasharray="
                        donutAnimated ? seg.dashArray : `0 ${seg.circumference}`
                      "
                      :stroke-dashoffset="seg.dashOffset"
                      :transform="'rotate(-90 60 60)'"
                      @mousemove="showTooltip($event, seg.tooltip)"
                      @mouseleave="hideTooltip"
                    />
                  </svg>
                </div>
                <div class="donut-legend">
                  <div v-if="visibleCats.ba" class="donut-item">
                    <span class="donut-dot ba"></span>
                    Lägenhetsbyten
                    <strong>{{ legendValue('ba') }}</strong>
                  </div>
                  <div v-if="visibleCats.ah" class="donut-item">
                    <span class="donut-dot ah"></span>
                    Andrahand
                    <strong>{{ legendValue('ah') }}</strong>
                  </div>
                  <div v-if="visibleCats.ovPrivate" class="donut-item">
                    <span class="donut-dot ov-private"></span>
                    Överlåtelser privat
                    <strong>{{ legendValue('ovPrivate') }}</strong>
                  </div>
                  <div v-if="visibleCats.ovCompany" class="donut-item">
                    <span class="donut-dot ov-company"></span>
                    Överlåtelser företag
                    <strong>{{ legendValue('ovCompany') }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- STATISTIK TAB -->
      <div v-if="activeTab === 'statistics'" class="content statistics-content">
        <!-- BA -->
        <template v-if="activeCategory === 'ba'">
          <div class="applications-row card-full-width">
            <ChartCard
              gray
              title="Inkommande ärenden per månad"
              :subtitle="
                'Antal ärenden som skickats in till er under vald period' +
                (baApplicationsYear !== 'all'
                  ? ` (totalt <b>${baApplicationsTotal}</b>)`
                  : '')
              "
              class="applications-row-main"
              height500
            >
              <div class="monthly-chart-wrap">
                <YearPicker
                  :years="baApplicationsYears"
                  :model-value="baApplicationsYear"
                  @update:model-value="baApplicationsYear = $event"
                />
              </div>
              <ApplicationsChart
                :monthly="general.sentToHost"
                :selected-year="baApplicationsYear"
                @tooltip="tooltip = $event"
              />
            </ChartCard>
            <ChartCard
              gray
              :title="singleStatLabel"
              :subtitle="approvalSubtitle"
              class="applications-row-side"
            >
              <OutcomeDonut
                :items="approvalDonutItems"
                @tooltip="tooltip = $event"
              />
            </ChartCard>
          </div>
          <ChartCard gray title="Utfall av byten">
            <div class="monthly-chart-wrap">
              <YearPicker
                :years="baOutcomeYears"
                :model-value="baOutcomeYear"
                @update:model-value="baOutcomeYear = $event"
              />
            </div>
            <OutcomeDonut
              :items="outcomeItems(baOutcomeCounts, 'ba')"
              @tooltip="tooltip = $event"
            />
          </ChartCard>
          <ChartCard gray title="Hyresgäster som bytt från ert bestånd">
            <div class="monthly-chart-wrap">
              <YearPicker
                :years="baMovingYears"
                :model-value="baMovingYear"
                @update:model-value="baMovingYear = $event"
              />
            </div>
            <OutcomeDonut
              :items="movingItems(baMovingCounts)"
              @tooltip="tooltip = $event"
            />
          </ChartCard>
        </template>

        <!-- OV -->
        <template v-if="isOvCategory">
          <BaseLoadingSpinner v-if="loadingAh" />
          <template v-else-if="analyticsData">
            <div class="applications-row card-full-width">
              <ChartCard
                gray
                title="Inkommande ärenden per månad"
                :subtitle="
                  'Antal ärenden som skickats in till er under vald period' +
                  (ovApplicationsYear !== 'all'
                    ? ` (totalt <b>${ovApplicationsTotal}</b>)`
                    : '')
                "
                class="applications-row-main"
                height500
              >
                <div class="monthly-chart-wrap">
                  <YearPicker
                    :years="ovApplicationsYears"
                    :model-value="ovApplicationsYear"
                    @update:model-value="ovApplicationsYear = $event"
                  />
                </div>
                <ApplicationsChart
                  :monthly="ovMonthlyCounts"
                  :selected-year="ovApplicationsYear"
                  @tooltip="tooltip = $event"
                />
              </ChartCard>
              <ChartCard
                gray
                :title="singleStatLabel"
                :subtitle="approvalSubtitle"
                class="applications-row-side"
              >
                <OutcomeDonut
                  :items="approvalDonutItems"
                  @tooltip="tooltip = $event"
                />
              </ChartCard>
            </div>
            <ChartCard gray title="Utfall av ärenden">
              <div class="monthly-chart-wrap">
                <YearPicker
                  :years="ovOutcomeYears"
                  :model-value="ovOutcomeYear"
                  @update:model-value="ovOutcomeYear = $event"
                />
              </div>
              <OutcomeDonut
                :items="outcomeItems(ovOutcomeCounts, 'ah')"
                @tooltip="tooltip = $event"
              />
            </ChartCard>
            <ChartCard
              v-if="activeCategory === 'ovPrivate'"
              gray
              title="Anledning till överlåtelse"
            >
              <div class="monthly-chart-wrap">
                <YearPicker
                  :years="ovReasonsYears"
                  :model-value="ovReasonsYear"
                  @update:model-value="ovReasonsYear = $event"
                />
              </div>
              <OutcomeDonut
                v-if="ovReasonsItems.length"
                :items="ovReasonsItems"
                @tooltip="tooltip = $event"
              />
              <p v-else>Ingen data</p>
            </ChartCard>
            <ChartCard
              v-if="
                activeCategory === 'ovPrivate' &&
                analyticsData.ovKuriosa?.private?.outcomePerReason?.length
              "
              gray
              title="Utfall per överlåtelseskäl"
              class="card-full-width"
            >
              <OutcomePerReasonChart
                :rows="analyticsData.ovKuriosa.private.outcomePerReason"
                :label-fn="ovReasonLabel"
              />
            </ChartCard>
            <ChartCard
              v-if="
                activeCategory === 'ovCompany' &&
                analyticsData.ovKuriosa?.company?.topBrokers?.length
              "
              gray
              title="Topp mäklare"
              class="card-full-width"
            >
              <ol class="top-brokers">
                <li
                  v-for="b in analyticsData.ovKuriosa.company.topBrokers"
                  :key="b.organizationNumber || b.name"
                  class="top-broker"
                >
                  <span class="top-broker-name">{{ b.name }}</span>
                  <span v-if="b.organizationNumber" class="top-broker-org"
                    >Org.nr {{ b.organizationNumber }}</span
                  >
                  <span class="top-broker-count">{{ b.count }} ärenden</span>
                </li>
              </ol>
            </ChartCard>
          </template>
        </template>

        <!-- AH -->
        <template v-if="activeCategory === 'ah'">
          <BaseLoadingSpinner v-if="loadingAh" />
          <template v-else-if="analyticsData">
            <div class="applications-row card-full-width">
              <ChartCard
                gray
                title="Inkommande ärenden per månad"
                :subtitle="
                  'Antal ärenden som skickats in till er under vald period' +
                  (ahApplicationsYear !== 'all'
                    ? ` (totalt <b>${ahApplicationsTotal}</b>)`
                    : '')
                "
                class="applications-row-main"
                height500
              >
                <div class="monthly-chart-wrap">
                  <YearPicker
                    :years="ahApplicationsYears"
                    :model-value="ahApplicationsYear"
                    @update:model-value="ahApplicationsYear = $event"
                  />
                </div>
                <ApplicationsChart
                  :monthly="ahMonthlyCounts"
                  :selected-year="ahApplicationsYear"
                  @tooltip="tooltip = $event"
                />
              </ChartCard>
              <ChartCard
                gray
                :title="singleStatLabel"
                :subtitle="approvalSubtitle"
                class="applications-row-side"
              >
                <OutcomeDonut
                  :items="approvalDonutItems"
                  @tooltip="tooltip = $event"
                />
              </ChartCard>
            </div>
            <ChartCard gray title="Utfall av ärenden">
              <div class="monthly-chart-wrap">
                <YearPicker
                  :years="ahOutcomeYears"
                  :model-value="ahOutcomeYear"
                  @update:model-value="ahOutcomeYear = $event"
                />
              </div>
              <OutcomeDonut
                :items="outcomeItems(ahOutcomeCounts, 'ah')"
                @tooltip="tooltip = $event"
              />
            </ChartCard>
            <ChartCard gray title="Anledning till uthyrning">
              <div class="monthly-chart-wrap">
                <YearPicker
                  :years="ahReasonsYears"
                  :model-value="ahReasonsYear"
                  @update:model-value="ahReasonsYear = $event"
                />
              </div>
              <OutcomeDonut
                v-if="ahReasonsItems.length"
                :items="ahReasonsItems"
                @tooltip="tooltip = $event"
              />
              <p v-else>Ingen data</p>
            </ChartCard>
            <ChartCard
              v-if="rentalPeriodBars.length"
              gray
              title="Uthyrningsperiodens längd"
              class="card-full-width"
            >
              <div class="distribution-bars">
                <div
                  v-for="b in rentalPeriodBars"
                  :key="b.key"
                  class="distribution-bar-group"
                >
                  <span class="distribution-label">{{ b.label }}</span>
                  <div class="distribution-track">
                    <div
                      class="distribution-bar"
                      :style="{ width: b.percent + '%' }"
                    ></div>
                  </div>
                  <span class="distribution-count"
                    >{{ b.count }} ({{ b.percent }}%)</span
                  >
                </div>
              </div>
            </ChartCard>
            <ChartCard
              v-if="analyticsData.ahKuriosa?.outcomePerReason?.length"
              gray
              title="Utfall per uthyrningsskäl"
              class="card-full-width"
            >
              <OutcomePerReasonChart
                :rows="analyticsData.ahKuriosa.outcomePerReason"
                :label-fn="ahReasonLabel"
              />
            </ChartCard>
          </template>
        </template>
      </div>

      <!-- RENSADE ÄRENDEN TAB -->
      <div v-if="activeTab === 'filtering'" class="content filtering-content">
        <BaseLoadingSpinner v-if="filteringLoading" />
        <template
          v-else-if="filteringData && Object.keys(filteringData).length"
        >
          <div class="filtering-controls">
            <div class="year-picker">
              <button
                class="year-btn"
                :class="{ active: selectedFilterYear === 'all' }"
                @click="selectedFilterYear = 'all'"
              >
                Alla år
              </button>
              <button
                v-for="year in filteringYears"
                :key="year"
                class="year-btn"
                :class="{ active: selectedFilterYear === year }"
                @click="selectedFilterYear = year"
              >
                {{ year }}
              </button>
            </div>
          </div>

          <div class="filtering-layout">
            <ChartCard gray :title="'Per månad ' + selectedFilterYear">
              <div class="stacked-chart">
                <div class="chart-y-axis">
                  <span
                    v-for="tick in filteringYTicks"
                    :key="tick"
                    class="y-tick"
                  >
                    {{ tick }}
                  </span>
                </div>
                <div class="chart-bars">
                  <div
                    v-for="m in filteringMonths"
                    :key="m.month"
                    class="bar-group"
                  >
                    <div class="stacked-bar">
                      <div
                        class="bar-segment sent"
                        :style="{
                          height: (barsAnimated ? filterBarH(m.sent) : 0) + '%'
                        }"
                        :data-tooltip="sentTooltip(m)"
                      ></div>
                      <div
                        class="bar-segment filtered"
                        :style="{
                          height:
                            (barsAnimated ? filterBarH(m.filtered) : 0) + '%'
                        }"
                        :data-tooltip="filteredTooltip(m)"
                      ></div>
                    </div>
                    <span class="bar-label">{{ monthLabel(m.month) }}</span>
                  </div>
                </div>
              </div>
              <div class="chart-legend">
                <span class="legend-item">
                  <span class="legend-dot sent"></span> Skickade
                </span>
                <span class="legend-item">
                  <span class="legend-dot filtered"></span> Rensade
                </span>
              </div>
            </ChartCard>

            <div class="filtering-sidebar">
              <ChartCard gray title="Översikt" class="applications-row-side">
                <OutcomeDonut
                  :items="filteringDonutItems"
                  :show-total="true"
                  @tooltip="tooltip = $event"
                />
              </ChartCard>
            </div>
          </div>
        </template>
        <div v-else class="no-data">Ingen rensningsdata tillgänglig</div>
      </div>
    </div>

    <div
      v-if="tooltip.visible"
      class="donut-tooltip"
      :style="{ left: tooltip.x + '%', top: tooltip.y + '%' }"
    >
      {{ tooltip.text }}
    </div>
  </DefaultPage>
</template>

<script>
import { mapGetters } from 'vuex';
import { Chart as ChartJS, Tooltip, Legend } from 'chart.js';
import {
  getDashboardStatistics,
  getDashboardAnalytics
} from '@/services/baService';
import DefaultPage from '@/components/DefaultPage.vue';
import ChartCard from '@/components/ChartCard.vue';
import ApplicationsChart from '@/components/Charts/ApplicationsChart.vue';
import OutcomeDonut from '@/components/Charts/OutcomeDonut.vue';
import OutcomePerReasonChart from '@/components/Charts/OutcomePerReasonChart.vue';
import YearPicker from '@/components/YearPicker.vue';
import BaseLoadingSpinner from '@/components/BaseLoadingSpinner.vue';

ChartJS.register(Tooltip, Legend);
ChartJS.defaults.font.family = 'Averta Std, Helvetica, Arial, sans-serif';
ChartJS.defaults.font.size = 14;
ChartJS.defaults.font.weight = '600';

const AH_REASON_LABELS = {
  '': 'Ej angivet',
  arbete: 'Arbete',
  studie: 'Studier',
  sambo: 'Sambo',
  utlandsvistelse: 'Utlandsvistelse',
  other: 'Övrigt'
};

const OV_REASON_LABELS = {
  '': 'Ej angivet',
  sambo: 'Samboende',
  separation: 'Separation',
  other: 'Övrigt'
};

const RENTAL_PERIOD_LABELS = {
  m1to3: '1–3 mån',
  m3to6: '3–6 mån',
  m6to12: '6–12 mån',
  m12plus: '12+ mån'
};

function extractCountsByYear(monthly, key) {
  if (!monthly) return {};
  const result = {};
  for (const [year, yearData] of Object.entries(monthly)) {
    result[year] = {};
    for (const [month, d] of Object.entries(yearData || {})) {
      result[year][month] = (d && d[key]) || 0;
    }
  }
  return result;
}

function niceStep(max, divisions = 4) {
  if (!max || max <= 0) return 1;
  const rough = max / divisions;
  if (rough <= 1) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const normalized = rough / magnitude;
  let nice;
  if (normalized <= 1) nice = 1;
  else if (normalized <= 2) nice = 2;
  else if (normalized <= 2.5) nice = 2.5;
  else if (normalized <= 5) nice = 5;
  else nice = 10;
  return nice * magnitude;
}

function niceTicks(max) {
  if (!max || max <= 0) return [0];
  const step = niceStep(max);
  const ticks = [0];
  let v = step;
  while (v < max) {
    ticks.push(v);
    v += step;
  }
  ticks.push(v);
  return ticks.reverse();
}

function sumYearCounts(monthly, selectedYear) {
  if (!monthly) return 0;
  let total = 0;
  for (const [year, yearData] of Object.entries(monthly)) {
    if (selectedYear !== 'all' && year !== selectedYear) continue;
    for (const v of Object.values(yearData || {})) {
      total += Number(v) || 0;
    }
  }
  return total;
}

function sumCountsByYear(countsByYear, selectedYear, keys) {
  const result = {};
  for (const key of keys) result[key] = 0;
  if (!countsByYear) return result;
  for (const [year, data] of Object.entries(countsByYear)) {
    if (selectedYear !== 'all' && year !== selectedYear) continue;
    for (const key of keys) {
      result[key] += Number(data?.[key]) || 0;
    }
  }
  return result;
}

const REASON_COLORS = [
  '#2c3e50',
  '#409fff',
  '#ee9b39',
  '#ff3843',
  '#878b9d',
  '#339d64',
  '#6b4a8a',
  '#3a6b8a'
];

export default {
  components: {
    ChartCard,
    ApplicationsChart,
    OutcomeDonut,
    OutcomePerReasonChart,
    YearPicker,
    BaseLoadingSpinner,
    DefaultPage
  },
  data() {
    return {
      allCategoryTabs: [
        { id: 'all', label: 'Alla moduler', slug: 'allt' },
        {
          id: 'ba',
          label: 'Lägenhetsbyten',
          slug: 'byten',
          access: 'baAccess'
        },
        {
          id: 'ah',
          label: 'Andrahand',
          slug: 'andrahand',
          access: 'ahAccess'
        },
        {
          id: 'ovPrivate',
          label: 'Överlåtelser privat',
          slug: 'overlatelser-privat',
          access: 'ovPrivateAccess'
        },
        {
          id: 'ovCompany',
          label: 'Överlåtelser företag',
          slug: 'overlatelser-foretag',
          access: 'ovCompanyAccess'
        }
      ],
      activeTab: 'overview',
      // BA
      general: {},
      userStats: {},
      loadingGeneral: false,
      loadingUserStats: false,
      // AH/Analytics
      analyticsData: null,
      loadingAh: false,
      // Filtering
      filteringData: null,
      filteringAll: null,
      filteringLoading: false,
      selectedFilterYear: null,
      barsAnimated: false,
      // Year pickers för per-månad-charts
      baApplicationsYear: 'all',
      ahApplicationsYear: 'all',
      ovApplicationsYear: 'all',
      baOutcomeYear: 'all',
      baMovingYear: 'all',
      ahOutcomeYear: 'all',
      ovOutcomeYear: 'all',
      ahReasonsYear: 'all',
      ovReasonsYear: 'all',
      // Översikt (flyttad från AnalyticsDashboard)
      overviewYear: 'all',
      overviewBarsAnimated: false,
      donutAnimated: false,
      tooltip: { visible: false, text: '', x: 0, y: 0 }
    };
  },
  computed: {
    ...mapGetters({
      baAccess: 'app/baAccess',
      ahAccess: 'app/ahAccess',
      ovPrivateAccess: 'app/ovPrivateAccess',
      ovCompanyAccess: 'app/ovCompanyAccess'
    }),
    accessMap() {
      return {
        baAccess: this.baAccess,
        ahAccess: this.ahAccess,
        ovPrivateAccess: this.ovPrivateAccess,
        ovCompanyAccess: this.ovCompanyAccess
      };
    },
    visibleCats() {
      const cat = this.activeCategory;
      return {
        ba: (cat === 'all' || cat === 'ba') && this.baAccess,
        ah: (cat === 'all' || cat === 'ah') && this.ahAccess,
        ovPrivate:
          (cat === 'all' || cat === 'ovPrivate') && this.ovPrivateAccess,
        ovCompany:
          (cat === 'all' || cat === 'ovCompany') && this.ovCompanyAccess
      };
    },
    categoryTabs() {
      return this.allCategoryTabs.filter(
        cat => !cat.access || this.accessMap[cat.access]
      );
    },
    activeCategory() {
      const slug = this.$route.params.category;
      const match = this.allCategoryTabs.find(c => c.slug === slug);
      return match ? match.id : 'all';
    },
    singleStatLabel() {
      const map = {
        ba: 'Godkännandegrad — Byten',
        ah: 'Godkännandegrad — Andrahand',
        ovPrivate: 'Godkännandegrad — Överlåtelser privat',
        ovCompany: 'Godkännandegrad — Överlåtelser företag'
      };
      return map[this.activeCategory] || '';
    },
    singleStatValue() {
      const items = this.approvalDonutItems;
      if (!items.length) return null;
      const approved = items.find(i => i.key === 'approved')?.value || 0;
      const rejected = items.find(i => i.key === 'rejected')?.value || 0;
      const total = approved + rejected;
      return total ? Math.round((approved / total) * 100) : null;
    },
    singleStatTotal() {
      return this.approvalDonutItems.reduce(
        (s, i) => s + (Number(i.value) || 0),
        0
      );
    },
    approvalSubtitle() {
      if (this.singleStatValue == null) return '';
      const totalPart = this.singleStatTotal
        ? ` av totalt <b>${this.singleStatTotal}</b> avslutade`
        : '';
      return `<b>${this.singleStatValue}%</b> godkändes${totalPart}`;
    },
    currentTabs() {
      const tabs = [];
      if (this.activeCategory === 'all') {
        tabs.push({ id: 'overview', label: 'Översikt' });
      } else {
        tabs.push({ id: 'statistics', label: 'Allmänt' });
      }
      tabs.push({ id: 'filtering', label: 'Rensade ärenden' });
      return tabs;
    },
    ahReasonsYears() {
      return Object.keys(this.analyticsData?.ahReasons || {})
        .filter(y => /^\d{4}$/.test(y))
        .sort();
    },
    ahReasonsItems() {
      return this.buildReasonItems(
        this.analyticsData?.ahReasons,
        this.ahReasonsYear,
        AH_REASON_LABELS
      );
    },
    ovReasonsYears() {
      return Object.keys(this.analyticsData?.ovReasons || {})
        .filter(y => /^\d{4}$/.test(y))
        .sort();
    },
    ovReasonsItems() {
      return this.buildReasonItems(
        this.analyticsData?.ovReasons,
        this.ovReasonsYear,
        OV_REASON_LABELS
      );
    },
    ahMonthlyCounts() {
      return extractCountsByYear(this.analyticsData?.monthly, 'ah');
    },
    ahApplicationsYears() {
      return Object.keys(this.ahMonthlyCounts).sort();
    },
    ahApplicationsTotal() {
      return sumYearCounts(this.ahMonthlyCounts, this.ahApplicationsYear);
    },
    baApplicationsYears() {
      return Object.keys(this.general?.sentToHost || {}).sort();
    },
    baApplicationsTotal() {
      return sumYearCounts(this.general?.sentToHost, this.baApplicationsYear);
    },
    baOutcomeYears() {
      return Object.keys(this.general?.countsByYear || {})
        .filter(y => /^\d{4}$/.test(y))
        .sort();
    },
    baOutcomeCounts() {
      return sumCountsByYear(this.general?.countsByYear, this.baOutcomeYear, [
        'approved',
        'rejected',
        'cancelled',
        'removed'
      ]);
    },
    baMovingYears() {
      return Object.keys(this.userStats?.movingWithinLandlordByYear || {})
        .filter(y => /^\d{4}$/.test(y))
        .sort();
    },
    baMovingCounts() {
      return sumCountsByYear(
        this.userStats?.movingWithinLandlordByYear,
        this.baMovingYear,
        ['same', 'other']
      );
    },
    ahOutcomeYears() {
      return Object.keys(this.analyticsData?.counts?.ah || {})
        .filter(y => /^\d{4}$/.test(y))
        .sort();
    },
    ahOutcomeCounts() {
      return sumCountsByYear(
        this.analyticsData?.counts?.ah,
        this.ahOutcomeYear,
        ['approved', 'rejected', 'aborted']
      );
    },
    ovOutcomeYears() {
      return Object.keys(this.analyticsData?.counts?.[this.ovCountsKey] || {})
        .filter(y => /^\d{4}$/.test(y))
        .sort();
    },
    ovOutcomeCounts() {
      return sumCountsByYear(
        this.analyticsData?.counts?.[this.ovCountsKey],
        this.ovOutcomeYear,
        ['approved', 'rejected', 'aborted']
      );
    },
    ovMonthlyCounts() {
      return extractCountsByYear(this.analyticsData?.monthly, this.ovCountsKey);
    },
    ovApplicationsYears() {
      return Object.keys(this.ovMonthlyCounts).sort();
    },
    ovApplicationsTotal() {
      return sumYearCounts(this.ovMonthlyCounts, this.ovApplicationsYear);
    },
    parsedMonths() {
      if (!this.analyticsData?.monthly) return [];
      const months = [];
      for (const [, yearData] of Object.entries(this.analyticsData.monthly)) {
        for (const [month, d] of Object.entries(yearData)) {
          months.push({
            month,
            ba: d.ba || 0,
            ah: d.ah || 0,
            ovPrivate: d.ovPrivate || 0,
            ovCompany: d.ovCompany || 0
          });
        }
      }
      return months.sort((a, b) => a.month.localeCompare(b.month));
    },
    overviewYears() {
      return Object.keys(this.analyticsData?.monthly || {}).sort();
    },
    selectedMonthlyData() {
      const result = [];
      for (let i = 1; i <= 12; i++) {
        const mm = String(i).padStart(2, '0');
        const entry = {
          month:
            this.overviewYear === 'all' ? mm : `${this.overviewYear}-${mm}`,
          ba: 0,
          ah: 0,
          ovPrivate: 0,
          ovCompany: 0,
          byYear: {}
        };
        for (const row of this.parsedMonths) {
          const [year, rowMm] = row.month.split('-');
          if (rowMm !== mm) continue;
          if (this.overviewYear !== 'all' && year !== this.overviewYear) {
            continue;
          }
          const v = this.visibleCats;
          const ba = v.ba ? row.ba : 0;
          const ah = v.ah ? row.ah : 0;
          const ovPrivate = v.ovPrivate ? row.ovPrivate : 0;
          const ovCompany = v.ovCompany ? row.ovCompany : 0;
          entry.ba += ba;
          entry.ah += ah;
          entry.ovPrivate += ovPrivate;
          entry.ovCompany += ovCompany;
          entry.byYear[year] = { ba, ah, ovPrivate, ovCompany };
        }
        result.push(entry);
      }
      return result;
    },
    maxMonthlyTotal() {
      if (!this.selectedMonthlyData.length) return 1;
      return Math.max(
        ...this.selectedMonthlyData.map(
          m => m.ba + m.ah + m.ovPrivate + m.ovCompany
        ),
        1
      );
    },
    yTicks() {
      return niceTicks(this.maxMonthlyTotal);
    },
    approvalRates() {
      const rates = this.analyticsData?.summary?.approvalRate;
      if (!Array.isArray(rates)) {
        return { ba: null, ah: null, ovPrivate: null, ovCompany: null };
      }
      const calc = type => {
        const years =
          this.overviewYear === 'all'
            ? rates
            : rates.filter(y => y.year === this.overviewYear);
        const sent = years.reduce((s, y) => s + (y[type]?.sent || 0), 0);
        const approved = years.reduce(
          (s, y) => s + (y[type]?.approved || 0),
          0
        );
        return sent ? Math.round((approved / sent) * 100) : null;
      };
      return {
        ba: calc('ba'),
        ah: calc('ah'),
        ovPrivate: calc('ovPrivate'),
        ovCompany: calc('ovCompany')
      };
    },
    approvalDonutItems() {
      const type = this.activeCategory;
      if (!type || type === 'all') return [];
      const year = {
        ba: this.baApplicationsYear,
        ah: this.ahApplicationsYear,
        ovPrivate: this.ovApplicationsYear,
        ovCompany: this.ovApplicationsYear
      }[type];
      const source =
        type === 'ba'
          ? this.general?.countsByYear
          : this.analyticsData?.counts?.[type];
      if (!source) return [];
      const cancelledKeys =
        type === 'ba' ? ['cancelled', 'removed'] : ['aborted'];
      const counts = sumCountsByYear(source, year, [
        'approved',
        'rejected',
        ...cancelledKeys
      ]);
      const approved = counts.approved || 0;
      const rejected = counts.rejected || 0;
      const cancelled = cancelledKeys.reduce((s, k) => s + (counts[k] || 0), 0);
      const style = getComputedStyle(document.documentElement);
      const approvedColor =
        style.getPropertyValue('--cat-ba').trim() || '#409fff';
      const rejectedColor =
        style.getPropertyValue('--color-red').trim() || '#ff3843';
      const cancelledColor =
        style.getPropertyValue('--color-gold').trim() || '#ee9b39';
      return [
        {
          key: 'approved',
          label: 'Godkända',
          value: approved,
          color: approvedColor
        },
        {
          key: 'rejected',
          label: 'Ej godkända',
          value: rejected,
          color: rejectedColor
        },
        {
          key: 'cancelled',
          label: 'Avbrutna',
          value: cancelled,
          color: cancelledColor
        }
      ];
    },
    distributionCounts() {
      const months = this.selectedMonthlyData;
      return {
        ba: this.baAccess ? months.reduce((s, m) => s + (m.ba || 0), 0) : 0,
        ah: this.ahAccess ? months.reduce((s, m) => s + (m.ah || 0), 0) : 0,
        ovPrivate: this.ovPrivateAccess
          ? months.reduce((s, m) => s + (m.ovPrivate || 0), 0)
          : 0,
        ovCompany: this.ovCompanyAccess
          ? months.reduce((s, m) => s + (m.ovCompany || 0), 0)
          : 0
      };
    },
    distribution() {
      const { ba, ah, ovPrivate, ovCompany } = this.distributionCounts;
      const total = ba + ah + ovPrivate + ovCompany;
      if (!total) return { ba: 0, ah: 0, ovPrivate: 0, ovCompany: 0 };
      return {
        ba: Math.round((ba / total) * 100),
        ah: Math.round((ah / total) * 100),
        ovPrivate: Math.round((ovPrivate / total) * 100),
        ovCompany: Math.round((ovCompany / total) * 100)
      };
    },
    donutSegments() {
      const d = this.distribution;
      const counts = this.distributionCounts;
      const circumference = 2 * Math.PI * 45;
      const segments = [];
      let offset = 0;
      const style = getComputedStyle(document.documentElement);
      const colors = {
        ba: style.getPropertyValue('--cat-ba').trim(),
        ah: style.getPropertyValue('--cat-ah').trim(),
        ovPrivate: style.getPropertyValue('--cat-ov-private').trim(),
        ovCompany: style.getPropertyValue('--cat-ov-company').trim()
      };
      const labels = {
        ba: 'Lägenhetsbyten',
        ah: 'Andrahand',
        ovPrivate: 'Överlåtelser privat',
        ovCompany: 'Överlåtelser företag'
      };
      for (const key of ['ba', 'ah', 'ovPrivate', 'ovCompany']) {
        if (d[key] > 0) {
          const len = (d[key] / 100) * circumference;
          segments.push({
            color: colors[key],
            dashArray: `${len} ${circumference - len}`,
            dashOffset: -offset,
            circumference,
            tooltip: `${labels[key]}: ${counts[key]} (${d[key]}%)`
          });
          offset += len;
        }
      }
      return segments;
    },
    rentalPeriodBars() {
      const dist = this.analyticsData?.ahKuriosa?.rentalPeriodDistribution;
      if (!dist) return [];
      const total = Object.values(dist).reduce(
        (s, v) => s + (Number(v) || 0),
        0
      );
      if (!total) return [];
      return Object.entries(RENTAL_PERIOD_LABELS).map(([key, label]) => {
        const count = Number(dist[key]) || 0;
        return {
          key,
          label,
          count,
          percent: Math.round((count / total) * 100)
        };
      });
    },
    isOvCategory() {
      return (
        this.activeCategory === 'ovPrivate' ||
        this.activeCategory === 'ovCompany'
      );
    },
    ovCountsKey() {
      return this.activeCategory === 'ovCompany' ? 'ovCompany' : 'ovPrivate';
    },
    filteringYears() {
      if (!this.filteringData) return [];
      return Object.keys(this.filteringData).sort();
    },
    filteringMonths() {
      if (!this.filteringData || !this.selectedFilterYear) return [];
      const emptyMonth = m => ({
        month: m,
        sent: 0,
        filtered: 0,
        filteredZeroActivity: 0,
        filteredWithActivity: 0,
        byYear: {}
      });

      if (this.selectedFilterYear === 'all') {
        const combined = {};
        for (let i = 1; i <= 12; i++) {
          const mm = String(i).padStart(2, '0');
          combined[mm] = emptyMonth(`0000-${mm}`);
        }
        for (const [year, yearData] of Object.entries(this.filteringData)) {
          for (const [month, d] of Object.entries(yearData)) {
            const mm = month.slice(5);
            const bucket = combined[mm];
            if (!bucket) continue;
            bucket.sent += d.sent || 0;
            bucket.filtered += d.filtered || 0;
            bucket.filteredZeroActivity += d.filteredZeroActivity || 0;
            bucket.filteredWithActivity += d.filteredWithActivity || 0;
            bucket.byYear[year] = {
              sent: d.sent || 0,
              filtered: d.filtered || 0,
              filteredZeroActivity: d.filteredZeroActivity || 0,
              filteredWithActivity: d.filteredWithActivity || 0
            };
          }
        }
        return Object.values(combined);
      }

      const yearData = this.filteringData[this.selectedFilterYear] || {};
      const byMonth = {};
      for (let i = 1; i <= 12; i++) {
        const mm = String(i).padStart(2, '0');
        byMonth[mm] = emptyMonth(`${this.selectedFilterYear}-${mm}`);
      }
      for (const [month, d] of Object.entries(yearData)) {
        const mm = month.slice(5);
        if (!byMonth[mm]) continue;
        byMonth[mm] = {
          month,
          sent: d.sent || 0,
          filtered: d.filtered || 0,
          filteredZeroActivity: d.filteredZeroActivity || 0,
          filteredWithActivity: d.filteredWithActivity || 0,
          byYear: {}
        };
      }
      return Object.keys(byMonth)
        .sort()
        .map(k => byMonth[k]);
    },
    filteringTotals() {
      const months = this.filteringMonths;
      const sent = months.reduce((s, m) => s + m.sent, 0);
      const filtered = months.reduce((s, m) => s + m.filtered, 0);
      const filteredZeroActivity = months.reduce(
        (s, m) => s + m.filteredZeroActivity,
        0
      );
      const filteredWithActivity = months.reduce(
        (s, m) => s + m.filteredWithActivity,
        0
      );
      return {
        sent,
        filtered,
        filteredZeroActivity,
        filteredWithActivity,
        total: sent + filtered
      };
    },
    filteringDonutItems() {
      const t = this.filteringTotals;
      const style = getComputedStyle(document.documentElement);
      const sentColor = style.getPropertyValue('--cat-ba').trim() || '#409fff';
      const filteredColor =
        style.getPropertyValue('--color-red').trim() || '#ff3843';
      return [
        { key: 'sent', label: 'Skickade', value: t.sent, color: sentColor },
        {
          key: 'filtered',
          label: 'Rensade',
          value: t.filtered,
          color: filteredColor
        }
      ];
    },
    filteringMax() {
      if (!this.filteringMonths.length) return 1;
      return Math.max(...this.filteringMonths.map(m => m.sent + m.filtered), 1);
    },
    filteringYTicks() {
      return niceTicks(this.filteringMax);
    }
  },
  watch: {
    activeCategory() {
      this.activeTab = this.currentTabs[0]?.id || 'overview';
      this.filteringData = null;
    },
    activeTab(val) {
      if (val === 'filtering') {
        this.fetchFilteringData();
      }
      if (val === 'overview') {
        this.triggerOverviewAnimation();
      }
    },
    selectedFilterYear() {
      this.triggerBarAnimation();
    },
    overviewYear() {
      this.triggerOverviewAnimation();
    }
  },
  mounted() {
    this.ensureValidCategoryRoute();
    this.activeTab = this.currentTabs[0]?.id || 'overview';
    this.fetchDashboards().then(() => this.triggerOverviewAnimation());
  },
  methods: {
    ensureValidCategoryRoute() {
      const slug = this.$route.params.category;
      const match = this.allCategoryTabs.find(c => c.slug === slug);
      const hasAccess = !match?.access || this.accessMap[match.access];
      if (!match || !hasAccess) {
        this.$router.replace('/statistik/allt');
      }
    },
    goToCategory(cat) {
      if (this.$route.params.category === cat.slug) return;
      this.$router.push(`/statistik/${cat.slug}`);
    },
    async fetchDashboards() {
      this.loadingGeneral = true;
      this.loadingUserStats = true;
      this.loadingAh = true;
      try {
        const [stats, analytics] = await Promise.all([
          getDashboardStatistics(),
          getDashboardAnalytics()
        ]);
        this.applyDashboardData(stats, analytics);
      } catch (err) {
        console.error('Failed to load dashboards', err);
      }
      this.loadingGeneral = false;
      this.loadingUserStats = false;
      this.loadingAh = false;
    },
    applyDashboardData(stats, analytics) {
      const ba = stats?.ba || {};
      this.general = {
        countsByYear: ba.counts || {},
        sentToHost: this.deriveCounts(stats?.monthly, 'ba'),
        avgSwapTimeDays: analytics?.baKuriosa?.avgProcessingDays || {},
        avgMovingDistanceKm: ba.avgMovingDistanceKm,
        mostActiveArea: ba.mostActiveArea,
        mostPopularSwapMonth: ba.mostPopularSwapMonth
      };
      this.userStats = {
        movingWithinLandlordByYear: ba.movingWithinLandlord || {},
        avgYears: ba.avgYears,
        hadYearsList: ba.hadYearsList
      };
      this.analyticsData = {
        monthly: stats?.monthly || {},
        summary: { approvalRate: stats?.approvalRate || [] },
        counts: {
          ah: stats?.ah?.counts,
          ovPrivate: stats?.ovPrivate?.counts,
          ovCompany: stats?.ovCompany?.counts
        },
        ahReasons: analytics?.ahReasons || [],
        ovReasons: analytics?.ovReasons || [],
        ahKuriosa: analytics?.ahKuriosa || {},
        ovKuriosa: analytics?.ovKuriosa || {}
      };
      this.filteringAll = stats?.filtering || {};
    },
    deriveCounts(monthly, key) {
      if (!monthly) return {};
      const result = {};
      for (const [year, yearData] of Object.entries(monthly)) {
        result[year] = {};
        for (const [month, d] of Object.entries(yearData || {})) {
          result[year][month] = d?.[key] || 0;
        }
      }
      return result;
    },
    async fetchFilteringData() {
      this.filteringLoading = true;
      if (this.activeCategory === 'all') {
        this.filteringData = this.mergeFilteringAll();
      } else {
        const typeMap = {
          ba: 'ba',
          ah: 'ah',
          ovPrivate: 'ovPrivate',
          ovCompany: 'ovCompany'
        };
        const key = typeMap[this.activeCategory] || 'ba';
        this.filteringData = this.filteringAll?.[key] || {};
      }
      this.selectedFilterYear = 'all';
      this.filteringLoading = false;
    },
    mergeFilteringAll() {
      const combined = {};
      for (const moduleData of Object.values(this.filteringAll || {})) {
        for (const [year, yearData] of Object.entries(moduleData || {})) {
          if (!combined[year]) combined[year] = {};
          for (const [month, d] of Object.entries(yearData || {})) {
            if (!combined[year][month]) {
              combined[year][month] = {
                sent: 0,
                filtered: 0,
                filteredZeroActivity: 0,
                filteredWithActivity: 0
              };
            }
            combined[year][month].sent += d.sent || 0;
            combined[year][month].filtered += d.filtered || 0;
            combined[year][month].filteredZeroActivity +=
              d.filteredZeroActivity || 0;
            combined[year][month].filteredWithActivity +=
              d.filteredWithActivity || 0;
          }
        }
      }
      return combined;
    },
    buildReasonItems(byYear, selectedYear, labelMap) {
      if (!byYear) return [];
      const summed = {};
      for (const [year, list] of Object.entries(byYear)) {
        if (selectedYear !== 'all' && year !== selectedYear) continue;
        for (const r of list || []) {
          const key = r.type ?? 'null';
          summed[key] = (summed[key] || 0) + (Number(r.count) || 0);
        }
      }
      const entries = Object.entries(summed)
        .map(([type, count]) => ({ type, count }))
        .sort((a, b) => b.count - a.count);
      return entries.map((r, i) => ({
        key: r.type,
        label: labelMap[r.type] || r.type || 'Ej angivet',
        value: r.count,
        color: REASON_COLORS[i % REASON_COLORS.length]
      }));
    },
    outcomeItems(counts, type) {
      const style = getComputedStyle(document.documentElement);
      const approved = style.getPropertyValue('--cat-ba').trim() || '#409fff';
      const rejected =
        style.getPropertyValue('--color-red').trim() || '#ff3843';
      const gold = style.getPropertyValue('--color-gold').trim() || '#ee9b39';
      const grey = style.getPropertyValue('--color-grey').trim() || '#878b9d';
      const items = [
        {
          key: 'approved',
          label: 'Godkända',
          value: counts?.approved,
          color: approved
        },
        {
          key: 'rejected',
          label: 'Nekade',
          value: counts?.rejected,
          color: rejected
        }
      ];
      if (type === 'ba') {
        items.push({
          key: 'cancelled',
          label: 'Avbrutna',
          value: counts?.cancelled,
          color: gold
        });
        items.push({
          key: 'removed',
          label: 'Borttagna',
          value: counts?.removed,
          color: grey
        });
      } else {
        items.push({
          key: 'aborted',
          label: 'Avbrutna',
          value: counts?.aborted,
          color: gold
        });
      }
      return items;
    },
    movingItems(counts) {
      const style = getComputedStyle(document.documentElement);
      const ba = style.getPropertyValue('--cat-ba').trim() || '#409fff';
      const ah = style.getPropertyValue('--cat-ah').trim() || '#05337f';
      return [
        {
          key: 'same',
          label: 'Flyttar till samma hyresvärd',
          value: counts?.same,
          color: ba
        },
        {
          key: 'other',
          label: 'Byter hyresvärd vid flytt',
          value: counts?.other,
          color: ah
        }
      ];
    },
    legendValue(key) {
      const count = this.distributionCounts[key] || 0;
      if (!count) return '–';
      return `${count} (${this.distribution[key]}%)`;
    },
    showTooltip(event, text) {
      this.tooltip = {
        visible: true,
        text,
        x: event.clientX,
        y: event.clientY
      };
    },
    hideTooltip() {
      this.tooltip.visible = false;
    },
    triggerBarAnimation() {
      this.barsAnimated = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.barsAnimated = true;
        });
      });
    },
    triggerOverviewAnimation() {
      this.overviewBarsAnimated = false;
      this.donutAnimated = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.overviewBarsAnimated = true;
          this.donutAnimated = true;
        });
      });
    },
    barH(val) {
      if (!val) return 0;
      const topTick = this.yTicks[0] || this.maxMonthlyTotal;
      return Math.max(0.5, (val / topTick) * 95);
    },
    overviewBarTooltip(m, type, label) {
      if (this.overviewYear !== 'all') {
        return `${label}: ${m[type] || 0}`;
      }
      const years = Object.keys(m.byYear || {}).sort();
      if (!years.length) return `${label}: 0`;
      const parts = years.map(y => `${y}: ${m.byYear[y]?.[type] || 0}`);
      return `${label} — ${parts.join(', ')}`;
    },
    ahReasonLabel(type) {
      return AH_REASON_LABELS[type] || type || 'Ej angivet';
    },
    ovReasonLabel(type) {
      return OV_REASON_LABELS[type] || type || 'Ej angivet';
    },
    filterBarH(val) {
      if (!val) return 0;
      const topTick = this.filteringYTicks[0] || this.filteringMax;
      return Math.max(0.5, (val / topTick) * 95);
    },
    sentTooltip(m) {
      if (this.selectedFilterYear !== 'all') {
        return `Skickade: ${m.sent}`;
      }
      const years = Object.keys(m.byYear || {}).sort();
      if (!years.length) return 'Skickade: 0';
      const parts = years.map(y => `${y}: ${m.byYear[y]?.sent || 0}`);
      return `Skickade — ${parts.join(', ')}`;
    },
    filteredTooltip(m) {
      if (this.selectedFilterYear !== 'all') {
        return `Rensade: ${m.filtered} (utan aktivitet: ${m.filteredZeroActivity}, med aktivitet: ${m.filteredWithActivity})`;
      }
      const years = Object.keys(m.byYear || {}).sort();
      if (!years.length) return 'Rensade: 0';
      const parts = years.map(y => {
        const d = m.byYear[y] || {};
        return `${y}: ${d.filtered || 0} (${d.filteredZeroActivity || 0}/${
          d.filteredWithActivity || 0
        })`;
      });
      return `Rensade (utan/med) — ${parts.join(', ')}`;
    },
    monthLabel(ym) {
      if (!ym) return '';
      const m = ym.split('-')[1];
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'Maj',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Okt',
        'Nov',
        'Dec'
      ];
      return months[parseInt(m) - 1] || m;
    }
  }
};
</script>

<style scoped>
.stats-container {
  margin: 2rem 0 0 3.2rem;
  max-width: 1400px;
}

.stats-header {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin: 0 0 1.6rem;
}

.page-title {
  color: var(--color-dark-blue);
  font-size: 3.2rem;
  margin: 0;
}

.category-select-wrapper {
  position: relative;
}

.category-select {
  appearance: none;
  background: #fff;
  border: 1px solid #d0cec6;
  border-radius: 8px;
  padding: 0.8rem 3rem 0.8rem 1.4rem;
  font-size: 1.4rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--color-dark-blue);
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23555' stroke-width='2' fill='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
}

.category-select:focus {
  outline: none;
  border-color: var(--color-dark-blue);
}

/* Sub-tabbar (Översikt / Allmänt / Rensade) — underline */
.stats-tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e8e6de;
  margin-bottom: 0;
}

.stats-tab {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  color: #888;
  padding: 1rem 2rem;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition:
    color 0.2s,
    border-color 0.2s,
    background 0.2s;
}

.stats-tab:hover {
  color: var(--color-dark-blue);
}

.stats-tab.active {
  color: var(--color-dark-blue);
  border-bottom-color: var(--color-dark-blue);
}

/* Huvudtabbar (Alla moduler / Lägenhetsbyten / ...) — pillar */
.category-tabs {
  border-bottom: none;
  gap: 0.8rem;
  margin: 0 0 2rem;
  flex-wrap: wrap;
}

.category-tabs .stats-tab {
  border: 1px solid #d0cec6;
  border-radius: 999px;
  margin-bottom: 0;
  padding: 0.7rem 1.6rem;
  font-size: 1.3rem;
  background: #fff;
  color: var(--color-dark-blue);
}

.category-tabs .stats-tab:hover {
  border-color: var(--color-dark-blue);
}

.category-tabs .stats-tab.active {
  background: var(--color-dark-blue);
  color: #fff;
  border-color: var(--color-dark-blue);
}

.content.overview-content {
  display: block;
  width: 100%;
  grid-template-columns: none;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8e6de;
  padding: 2.4rem;
}

.content.statistics-content {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8e6de;
  padding: 2.4rem;
}

.stat-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin: 16px 0 24px;
}

.stat-cards .stat-card {
  min-width: 0;
}

.stat-card {
  background: #f8f7f7;
  border-radius: 10px;
  border: 1px solid #e8e6de;
  padding: 18px;
}

.stat-card-label {
  font-size: 1rem;
  color: #878b9d;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
}

.stat-card-value {
  font-size: 2.2rem;
  font-weight: 800;
  color: #2c3e50;
}

.overview-chart-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  align-items: stretch;
}

@media (min-width: 900px) {
  .overview-chart-row {
    grid-template-columns: minmax(0, 1fr) minmax(280px, 380px);
  }
}

.overview-side {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.overview-side .overview-chart-card.narrow {
  flex: 1;
}

.overview-chart-card.wide,
.overview-chart-card.narrow {
  display: flex;
  flex-direction: column;
}

.overview-chart-card {
  background: #f8f7f7;
  border-radius: 10px;
  border: 1px solid #e8e6de;
  padding: 20px;
}

.overview-chart-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #878b9d;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 16px 0;
}

.stacked-chart {
  display: flex;
  gap: 8px;
  flex: 1;
  min-height: 320px;
  align-items: stretch;
}

.chart-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  width: 30px;
  flex-shrink: 0;
}

.y-tick {
  font-size: 1rem;
  color: #878b9d;
  text-align: right;
}

.chart-bars {
  flex: 1;
  display: flex;
  align-items: flex-end;
  gap: 6px;
}

.bar-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  height: 100%;
}

.stacked-bar {
  display: flex;
  flex-direction: column-reverse;
  width: 100%;
  max-width: 40px;
  flex: 1;
}

.bar-segment {
  width: 100%;
  min-height: 0;
  transition: height 0.5s cubic-bezier(0.25, 0.1, 0.25, 1);
  position: relative;
  cursor: default;
}

.bar-segment[data-tooltip]:hover::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #2c3e50;
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  z-index: 10;
  pointer-events: none;
}

.bar-segment.ba,
.legend-dot.ba,
.donut-dot.ba {
  background: var(--cat-ba);
}

.bar-segment.ah,
.legend-dot.ah,
.donut-dot.ah {
  background: var(--cat-ah);
}

.bar-segment.ov-private,
.legend-dot.ov-private,
.donut-dot.ov-private {
  background: var(--cat-ov-private);
}

.bar-segment.ov-company,
.legend-dot.ov-company,
.donut-dot.ov-company {
  background: var(--cat-ov-company);
}

.bar-segment.sent,
.legend-dot.sent {
  background: var(--cat-ba);
}

.bar-segment.filtered,
.legend-dot.filtered {
  background: var(--color-red);
}

.bar-label {
  font-size: 1rem;
  color: #878b9d;
  margin-top: 6px;
}

.chart-legend {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 1.1rem;
  color: #878b9d;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.donut-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.donut-svg {
  width: 160px;
  height: 160px;
  flex-shrink: 0;
}

.donut-segment {
  transition:
    stroke-dasharray 0.8s cubic-bezier(0.25, 0.1, 0.25, 1),
    stroke-width 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  cursor: pointer;
}

.donut-segment:hover {
  stroke-width: 22;
}

.donut-tooltip {
  position: fixed;
  background: #2c3e50;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 4px;
  pointer-events: none;
  z-index: 1000;
  transform: translate(-50%, calc(-100% - 12px));
  white-space: nowrap;
}

.donut-legend {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.donut-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
  color: #5a6370;
}

.donut-item strong {
  margin-left: auto;
  color: #2c3e50;
}

.donut-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.monthly-chart-wrap {
  margin-bottom: 12px;
}

.kuriosa-value.red {
  color: #ff3843;
}

.distribution-bars {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.distribution-bar-group {
  display: grid;
  grid-template-columns: 100px 1fr 100px;
  align-items: center;
  gap: 12px;
}

.distribution-label {
  font-size: 1.2rem;
  color: #5a6370;
  text-align: right;
}

.distribution-track {
  height: 24px;
  background: #f0f0f2;
  border-radius: 4px;
  overflow: hidden;
}

.distribution-bar {
  height: 100%;
  background: #409fff;
  transition: width 0.5s ease;
}

.distribution-count {
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
}

.top-brokers {
  list-style: none;
  padding: 0;
  margin: 0;
  counter-reset: brokers;
}

.top-broker {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f2;
  counter-increment: brokers;
}

.top-broker::before {
  content: counter(brokers);
  font-weight: 800;
  font-size: 1.4rem;
  color: #878b9d;
  min-width: 24px;
}

.top-broker-name {
  font-weight: 700;
  color: #2c3e50;
  font-size: 1.3rem;
}

.top-broker-org {
  font-size: 1.1rem;
  color: #878b9d;
}

.top-broker-count {
  font-weight: 700;
  color: var(--color-blue);
  font-size: 1.2rem;
}

.content {
  margin: 1.6rem 0 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  width: 100%;
  max-width: 100%;
}

@media (min-width: 900px) {
  .content {
    grid-template-columns: 1fr 1fr;
  }
}

.analytics-content {
  display: block;
  width: 1400px;
}

.flowmap-content {
  display: block;
  width: 100%;
  max-width: 1400px;
}

.card-full-width {
  grid-column: 1 / 3;
}

.applications-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  align-items: stretch;
}

@media (min-width: 900px) {
  .applications-row {
    grid-template-columns: minmax(0, 1fr) minmax(280px, 380px);
  }
}

.applications-row-main {
  min-width: 0;
}

.applications-row-side {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.applications-row-side :deep(.card__body) {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.kuriosa-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.kuriosa-item {
  background: #f8f8fa;
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.kuriosa-value {
  font-size: 2.4rem;
  font-weight: 800;
  color: var(--color-dark-blue);
  display: block;
}

.kuriosa-label {
  font-size: 1.2rem;
  color: #878b9d;
  font-weight: 500;
}

.reasons-pie {
  max-width: 280px;
  margin: 0 auto;
}

.content > :not(.card-full-width) :deep(canvas) {
  max-height: 280px;
  margin: 0 auto;
}

.filtering-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  background-color: #fff;
  padding: 24px;
  border-radius: 12px;
}

.filtering-red {
  color: #ff3843 !important;
}

.year-picker {
  display: flex;
  gap: 4px;
}

.year-btn {
  background: #f8f8fa;
  border: 1px solid #e6e6e6;
  color: #878b9d;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.year-btn.active {
  background: var(--color-dark-blue);
  color: #fff;
  border-color: var(--color-dark-blue);
}

.filtering-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: stretch;
}

@media (min-width: 900px) {
  .filtering-layout {
    grid-template-columns: minmax(0, 1fr) minmax(280px, 340px);
  }
}

.filtering-layout :deep(.chart-card) {
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.filtering-layout :deep(.card__body) {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.filtering-layout .stacked-chart {
  height: 360px;
  flex: none;
}

.filtering-sidebar :deep(.chart-card) {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.filtering-summary {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  justify-content: center;
}

.filtering-bars {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  flex: 1;
  min-height: 260px;
  padding-top: 10px;
}

.filtering-bar-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.filtering-bar-stack {
  display: flex;
  flex-direction: column-reverse;
  width: 100%;
  max-width: 40px;
}

.filtering-bar {
  width: 100%;
  min-height: 0;
  transition: height 0.5s cubic-bezier(0.25, 0.1, 0.25, 1);
  position: relative;
  cursor: default;
}

.filtering-bar[data-tooltip]:hover::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #2c3e50;
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  z-index: 10;
}

.filtering-bar.sent {
  background: #409fff;
}

.filtering-bar.filtered {
  background: #ff3843;
}

.filtering-bar-label {
  font-size: 1rem;
  color: #878b9d;
  margin-top: 6px;
}

.filtering-legend {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 12px;
}

.filtering-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 1.1rem;
  color: #878b9d;
}

.filtering-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.filtering-dot.sent {
  background: #409fff;
}

.filtering-dot.filtered {
  background: #ff3843;
}

.filtering-pie-wrapper {
  max-width: 240px;
  margin: 0 auto;
}

.filtering-pie-total {
  text-align: center;
  margin-top: 12px;
  font-size: 1.2rem;
  color: #878b9d;
}

.filtering-pie-total strong {
  font-size: 1.6rem;
  font-weight: 800;
  color: var(--color-dark-blue);
}

.no-data {
  text-align: center;
  color: #878b9d;
  padding: 4rem;
  font-size: 1.3rem;
}
</style>
